import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { Form } from 'react-final-form'
import PageTitle from '../../components/page-title'
import AgreementPreviewModal from '../../components/agreement-preview-modal'
import TermsAndConditionAgreementStatus from '../../components/terms-and-condition-agreement-status/TermsAndConditionAgreementStatus'
import AgreementFormFields from '../create-terms-and-condition-agreement/AgreementFormFields'
import useAllClients from '../create-terms-and-condition-agreement/useAllClients'
import {
  validate,
  hasRequiredDraftValues,
  hasRequiredSelectValues,
  confirmAction
} from './utils'
import styles from './index.module.scss'

function EditFormHeader ({
  isDraft,
  values,
  invalid,
  isBusy,
  onSaveDraft,
  onPublish,
  onSaveChanges
}) {
  return (
    <div className={styles.pageHeader}>
      <PageTitle className={styles.pageTitle}>Edit Agreement</PageTitle>
      <div className={styles.actions}>
        {isDraft && (
          <>
            <button
              type='button'
              className='dis-btn dis-btn-lg dis-btn-blank'
              disabled={!hasRequiredDraftValues(values) || isBusy}
              onClick={onSaveDraft}>
              SAVE AS DRAFT
            </button>
            <button
              type='button'
              className='dis-btn dis-btn-lg dis-btn-primary'
              disabled={invalid || !hasRequiredSelectValues(values) || isBusy}
              onClick={onPublish}>
              SAVE AND PUBLISH
              <i className='material-icons dis-btn-icon'>check</i>
            </button>
          </>
        )}
        {!isDraft && (
          <button
            type='button'
            className='dis-btn dis-btn-lg dis-btn-primary'
            disabled={invalid || !hasRequiredSelectValues(values) || isBusy}
            onClick={onSaveChanges}>
            SAVE CHANGES
            <i className='material-icons dis-btn-icon'>check</i>
          </button>
        )}
      </div>
    </div>
  )
}

export default function EditTermsAndConditionAgreementForm ({
  initialValues,
  isDraft,
  agreementStatus,
  isDirtyRef,
  isSaving,
  isDeleting,
  isTogglingStatus,
  onSave,
  onStatusChange,
  onDestroy
}) {
  const submitStatusRef = useRef('published')
  const initialIsDefaultRef = useRef(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewContent, setPreviewContent] = useState('')
  const { clients, isClientsFetching } = useAllClients()

  useEffect(() => {
    initialIsDefaultRef.current = !!initialValues?.isDefault
  }, [initialValues])

  function openPreview (content) {
    setPreviewContent(content || '')
    setShowPreview(true)
  }

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={(values) => {
        const publish = isDraft ? submitStatusRef.current === 'published' : true
        onSave(values, publish)
      }}
      enableReinitialize>
      {({ handleSubmit, form, values, invalid, submitting, dirty }) => {
        isDirtyRef.current = dirty
        const isBusy = isSaving || submitting

        function confirmThenSubmit (publish) {
          const currentIsDefault = Boolean(values.isDefault)

          if (
            currentIsDefault !== initialIsDefaultRef.current &&
            !confirmAction('You have changed the default agreement setting. Do you want to save and continue?')
          ) {
            return
          }

          submitStatusRef.current = publish ? 'published' : 'draft'
          handleSubmit()
        }

        return (
          <form
            autoComplete='off'
            onSubmit={(event) => {
              event.preventDefault()
            }}>
            <EditFormHeader
              isDraft={isDraft}
              values={values}
              invalid={invalid}
              isBusy={isBusy}
              onSaveDraft={() => confirmThenSubmit(false)}
              onPublish={() => confirmThenSubmit(true)}
              onSaveChanges={() => confirmThenSubmit(true)}
            />

            <div className={cn(styles.statusPanel, 'dis-panel')}>
              <div className={styles.statusDisplay}>
                <TermsAndConditionAgreementStatus
                  status={agreementStatus}
                  onStatusChange={onStatusChange}
                  disabled={isTogglingStatus || isSaving || isDeleting}
                />
              </div>
            </div>

            <AgreementFormFields
              form={form}
              values={values}
              isDisabled={isBusy}
              editorId='edit-agreement-content'
              onPreview={openPreview}
            />

            <div className={styles.destroyAgreementContainer}>
              <button
                type='button'
                className='dis-btn dis-btn-blank dis-btn-sm'
                disabled={isSaving || isDeleting || submitting}
                onClick={onDestroy}>
                Destroy Agreement
                <i className='material-icons dis-btn-icon'>delete</i>
              </button>
            </div>

            <AgreementPreviewModal
              show={showPreview}
              onHide={() => setShowPreview(false)}
              content={previewContent}
              clients={clients}
              isClientsFetching={isClientsFetching}
            />
          </form>
        )
      }}
    </Form>
  )
}

EditFormHeader.propTypes = {
  isDraft: PropTypes.bool,
  values: PropTypes.object.isRequired,
  invalid: PropTypes.bool,
  isBusy: PropTypes.bool,
  onSaveDraft: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onSaveChanges: PropTypes.func.isRequired
}

EditTermsAndConditionAgreementForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  isDraft: PropTypes.bool,
  agreementStatus: PropTypes.string.isRequired,
  isDirtyRef: PropTypes.object.isRequired,
  isSaving: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isTogglingStatus: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onDestroy: PropTypes.func.isRequired
}
